import { PRICING } from '@/common/constants/pricing';
import { getDynamicCurrency } from '../../util-conversion/currency/currency-to-string';
import { VehiclePlan } from '../types';
import getPlanNameFromMiles from '@/account/utils/get-plan-name-from-miles';

export interface PlanCopy {
  id: number;
  planName: string;
  monthlyCostCopy: string;
  mileageIncludedCopy: string;
  overageCopy: string;
  commuterCopy?: string;
  isMostPopular: boolean;
  isZeroMilePlan: boolean;
  commuterMilesCopy?: string;
  perMonthMilageCopy: number;
}

export interface MileagePackageCopyProps {
  plan: VehiclePlan;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;
}

export function getMileagePackageCopy({
  plan,
  mostPopularPlanMileageAmount,
  splitPayment,
}: MileagePackageCopyProps): PlanCopy {
  const isZeroMilePlan = plan.miles_per_month === 0;

  const total_plan_price =
    plan.plan_price_per_mile * plan.miles_per_month + plan.base_price_per_month;
  const replenishment_miles_price =
    plan.replenishment_price_per_mile * plan.default_replenishment_miles_amount;
  const replenishment_price_per_mile_number = plan.replenishment_price_per_mile
    ? getDynamicCurrency(plan.replenishment_price_per_mile)
    : getDynamicCurrency(0);

  const monthlyCostCopy = (() => {
    if (splitPayment) {
      // Note: Split payment is only available for PAYG at this time so we return an empty string
      if (isZeroMilePlan) return '';

      const biWeeklyCost = getDynamicCurrency(total_plan_price / 2);
      return `${biWeeklyCost} x 2`;
    }

    const monthlyCost = getDynamicCurrency(total_plan_price);
    return `${monthlyCost}/mo${isZeroMilePlan ? ' + miles' : ''}`;
  })();

  const overageCopy = (() => {
    if (isZeroMilePlan && splitPayment) {
      return 'Currently not available for Split payment';
    }

    if (isZeroMilePlan) {
      return `Pay as you go: ${replenishment_price_per_mile_number}/mi`;
    }

    return `Additional miles: ${replenishment_price_per_mile_number}/mi`;
  })();

  const commuterCopy = (() => {
    switch (plan.miles_per_month) {
      case 0:
        return PRICING.PLAN_NAMES.ZERO_MILE;
      case 500:
        return PRICING.PLAN_NAMES.FIVE_HUNDRED_MILE;
      case 850:
        return PRICING.PLAN_NAMES.EIGHT_FIFTY_MILE;
      case 1200:
        return PRICING.PLAN_NAMES.TWELVE_HUNDRED_MILE;
      case 2000:
        return PRICING.PLAN_NAMES.EXTRA_HIGH_MILE;
      default:
        return undefined;
    }
  })();

  const commuterMilesCopy = (() => {
    switch (plan.miles_per_month) {
      case 0:
        return 'Mileage charged separately';
      case 500:
        return 'Drives up to 6,000 mi/yr';
      case 850:
        return 'Drives up to 12,000 mi/yr';
      case 1200:
        return 'Drives 12,000-24,000 mi/yr';
      case 2000:
        return 'Drives more than 24,000 mi/yr';
      default:
        return undefined;
    }
  })();

  return {
    id: plan.id,
    planName: getPlanNameFromMiles(plan.miles_per_month) ?? '',
    monthlyCostCopy,
    mileageIncludedCopy: isZeroMilePlan
      ? `${getDynamicCurrency(replenishment_miles_price)} charged for every ${plan.default_replenishment_miles_amount} miles`
      : `${plan.miles_per_month.toLocaleString()} miles / month included`,
    overageCopy: overageCopy,
    commuterCopy: commuterCopy,
    commuterMilesCopy: commuterMilesCopy,
    isMostPopular: mostPopularPlanMileageAmount === `${plan.miles_per_month}`,
    perMonthMilageCopy: plan.miles_per_month,
    isZeroMilePlan,
  };
}
